import {
  Box,
  Button,
  FormControl,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import * as Yup from 'yup';

import { TextInputFormField } from 'packages/common-frontend/components/FormField';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import {
  errorToastAdminConfig as errorToastConfigTemplate,
  successToastConfig as successToastConfigTemplate,
} from 'packages/common-frontend/constants';
import { Implementation } from 'packages/implementations/ui/loanProducts/LoanProductsTable';
import { GET_ORGANIZATION_LOAN_PRODUCTS } from 'packages/organization/requests/organization.gql';
import { useUpdateLoanProductNameMutation } from 'packages/organization/requests/organization.gql.generated';
import { Icon } from '~/packages/common-frontend/components/icons';
import { GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS } from '~/packages/implementations/requests/implementations.gql';

// Types
type LoanProductDetails = {
  id: string;
  name: string;
};

// Constants
// !TODO @eldarshamukhamedov Refactor to use zod
const validationSchema = Yup.object().shape({
  name: Yup.string().trim().strict().required(),
});

// Fragments
type EditLoanProductFormProps = {
  implementation: Implementation;
  onSuccess: () => void;
  loanProductDetails: LoanProductDetails;
};
const EditLoanProductForm = ({
  implementation,
  loanProductDetails: { id, name: oldName },
  onSuccess,
}: EditLoanProductFormProps) => {
  const toast = useToast();

  const [updateLoanProductName] = useUpdateLoanProductNameMutation({
    refetchQueries: [
      {
        query: GET_ORGANIZATION_LOAN_PRODUCTS,
        variables: { organizationId: implementation.borrowerOrganization.id },
      },
      {
        query: GET_ORGANIZATION_IMPLEMENTATIONS_DETAILS,
        variables: { organizationId: implementation.ownerOrganization.id },
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: ({ updateLoanProductName: res }) => {
      toast({
        ...successToastConfigTemplate,
        id: `${id}-${res.name}-updateLoanProductName-toast`,
        title: `Updated loan product with name: ${res.name}`,
      });
    },
    onError: () => {
      toast({
        ...errorToastConfigTemplate,
        id: 'errorUpdateLoanProductName',
        title: 'Error saving updated loan product name.',
      });
    },
  });

  return (
    <Box>
      <Formik
        initialValues={{ name: oldName }}
        validateOnChange
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await updateLoanProductName({
            variables: {
              id,
              newName: values.name,
            },
          });
          onSuccess();
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <VStack spacing="10px">
              <FormControl>
                <TextInputFormField label="Name" name="name" />
              </FormControl>
              <Button type="submit" isDisabled={isSubmitting}>
                Submit
              </Button>
            </VStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

// Exports
type EditLoanProductButtonProps = {
  implementation: Implementation;
  loanProductDetails: LoanProductDetails;
};
export const EditLoanProductButton = ({
  implementation,
  loanProductDetails,
}: EditLoanProductButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <IconButtonV2
        aria-label="Edit"
        icon={<Icon icon="edit" />}
        label="Edit"
        onClick={() => setIsOpen(true)}
        variant="v2-ghost"
        colorScheme="gray"
        placement="bottom"
      />

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalOverlay />
        <ModalContent padding="20px">
          <ModalHeader>Edit Loan Product</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <EditLoanProductForm
              implementation={implementation}
              onSuccess={() => setIsOpen(false)}
              loanProductDetails={loanProductDetails}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
